import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom'
import AuthenticatedLayout from './components/layout/AuthenticatedLayout'
import Dashboard from './components/dashboard/Dashboard'
import MissionControl from './components/mission-control/MissionControl'
import Workspace from './components/workspace/Workspace'
import ShareView from './components/share/ShareView'
import AuthPage from './components/auth/AuthPage'
import LandingPage from './components/landing/LandingPage'
import FeaturesPage from './components/landing/FeaturesPage'
// import DocsPage from './components/landing/DocsPage'
import ComingSoonPage from './components/landing/ComingSoonPage'
import TermsPage from './components/landing/TermsPage'
import PrivacyPage from './components/landing/PrivacyPage'
import AdminDashboard from './components/admin/AdminDashboard'
import MachineProfilesModal from './components/mission-control/MachineProfilesModal'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import type { Job, Operation } from './types'
import type { JobWizardConfig } from './types'
import { useMachineProfiles } from './hooks/useMachineProfiles'
import { useToolInventory } from './hooks/useToolInventory'
import { useJobs } from './hooks/useJobs'
import { generateShareToken } from './lib/tokens'
import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react'
import OnboardingFlow from './components/onboarding/OnboardingFlow'
import BugReportPage from './components/bug-report/BugReportPage'

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center">
      <p className="font-mono text-sm text-slate-500 animate-pulse">Loading…</p>
    </div>
  )
}

const isAdminDomain = window.location.hostname.startsWith('admin.') || window.location.hostname.startsWith('beta.admin.')

/** Resolves the job from URL params, fetching from DB if needed */
function WorkspaceLoader({
  jobs,
  reopenJob,
  setActiveJobId,
  renderWorkspace,
}: {
  jobs: Job[]
  reopenJob: (id: string) => Promise<void>
  setActiveJobId: (id: string) => void
  renderWorkspace: (job: Job) => ReactNode
}) {
  const { jobId } = useParams<{ jobId: string }>()
  const [notFound, setNotFound] = useState(false)
  const [loading, setLoading] = useState(false)
  const attemptedRef = useRef<string | null>(null)

  const job = jobs.find(j => j.id === jobId) ?? null

  useEffect(() => {
    if (!jobId || notFound) return
    if (job) {
      // Job is in local state — just set it active
      setActiveJobId(jobId)
      return
    }
    // Avoid re-fetching if we already tried this ID
    if (attemptedRef.current === jobId) return
    attemptedRef.current = jobId

    setLoading(true)
    reopenJob(jobId)
      .then(() => {
        // reopenJob will add it to jobs state if found; if not, jobs won't change
        // We check in the next render cycle via the `job` variable
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [jobId, job, reopenJob, setActiveJobId, notFound])

  // After reopenJob completes, check if it was actually found
  useEffect(() => {
    if (!loading && attemptedRef.current === jobId && !job) {
      setNotFound(true)
    }
  }, [loading, jobId, job])

  if (notFound) return <Navigate to="/dashboard" replace />
  if (!job) return <LoadingScreen />

  return <>{renderWorkspace(job)}</>
}

function AppContent() {
  const navigate = useNavigate()
  const { user, loading: authLoading, isAdmin, signOut } = useAuth()
  const [showProfilesModal, setShowProfilesModal] = useState(false)

  const { profiles, addProfile, updateProfile, removeProfile, loading: _profilesLoading } =
    useMachineProfiles()

  const { tools: inventoryTools, setTools: setInventoryTools, loading: _inventoryLoading } =
    useToolInventory()

  const {
    jobs,
    setJobs,
    activeJob: _activeJob,
    activeJobId: _activeJobId,
    setActiveJobId,
    loading: _jobsLoading,
    addJob,
    closeJob: _closeJob,
    deleteJob,
    reopenJob,
    updateOperationGCode,
    updateStreamingOperationGCode,
    updateJobFields,
    addOperation,
    removeOperation,
    appendMessage,
  } = useJobs()

  // Track which message IDs have already been persisted to avoid duplicate inserts
  // during rapid streaming updates (React batches state, so `jobs` can be stale)
  const persistedMsgIds = useRef(new Set<string>())

  // Pre-seed with message IDs that already exist (loaded from DB or inserted via addJob)
  useEffect(() => {
    for (const job of jobs) {
      for (const msg of job.messages) {
        persistedMsgIds.current.add(msg.id)
      }
    }
  }, [jobs])

  // All hooks must be above early returns (React rules of hooks)
  const handleOnboardingComplete = useCallback(async (machine: import('./types').MachineProfile, tools: import('./types').Tool[]) => {
    await addProfile(machine)
    if (tools.length > 0) setInventoryTools(tools)
    localStorage.setItem('gcodr_onboarding_done', '1')
    navigate('/new-job')
  }, [addProfile, setInventoryTools, navigate])

  if (authLoading || _jobsLoading || _profilesLoading || _inventoryLoading) return <LoadingScreen />

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={isAdminDomain ? <AuthPage /> : <LandingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/docs" element={<ComingSoonPage title="Docs" />} />
        <Route path="/docs/:slug" element={<ComingSoonPage title="Docs" />} />
        <Route path="/pricing" element={<ComingSoonPage title="Pricing" />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  // On admin domains, redirect all non-admin paths to /admin
  if (isAdminDomain && isAdmin && !window.location.pathname.startsWith('/admin')) {
    return <Navigate to="/admin" replace />
  }

  const handleManageProfiles = () => {
    setShowProfilesModal(true)
  }

  const handleAddJob = async (config: JobWizardConfig) => {
    const machine = profiles.find(p => p.id === config.machineId)!
    const jobId = crypto.randomUUID()
    const baseLabel = `${config.material} · ${machine.name}`
    const clashCount = jobs.filter(
      j => j.label === baseLabel || j.label.startsWith(baseLabel + ' #'),
    ).length
    const label = clashCount > 0 ? `${baseLabel} #${clashCount + 1}` : baseLabel

    // Create a default operation — agent generates G-code on first chat message
    const defaultOp: Operation = {
      id: crypto.randomUUID(),
      label: 'Op 10',
      operationType: config.operationType,
      workOffsets: config.workOffsets,
      gcode: null,
      isGenerating: false,
    }

    // Seed memory from wizard knowledge base entries (drawing analysis, tool suggestions, validation)
    const seedMemory = config.knowledgeBase.length > 0
      ? config.knowledgeBase.map(kb => `[${kb.source}]\n${kb.content}`).join('\n\n')
      : ''

    const job: Job = {
      id: jobId,
      label,
      machineId: config.machineId,
      tools: config.tools,
      material: config.material,
      stockShape: config.stockShape,
      notes: config.notes,
      drawing: config.drawing,
      stockSize: config.stockSize,
      messages: [],
      units: 'metric',
      shareToken: generateShareToken(),
      closedAt: null,
      operations: [defaultOp],
      activeOperationId: defaultOp.id,
      agentSessionId: null,
      memory: seedMemory,
      pipelineSteps: [],
      extractedGeometry: config.extractedGeometry,
      pdfBase64: config.pdfBase64,
    }

    // Await job persistence before navigating — prevents race where
    // auto-gen messages arrive before the job exists in DB (404)
    try {
      await addJob(job)
    } catch (err) {
      console.error('Failed to persist job:', err)
      // Job is in local state — navigate anyway, messages will retry on next interaction
    }
    setActiveJobId(jobId)
    navigate(`/workspace/${jobId}`)
  }

  const handleReopenJob = async (id: string) => {
    await reopenJob(id)
    navigate(`/workspace/${id}`)
  }

  /**
   * Dispatcher that keeps Workspace's `updateJob: (updates: Partial<Job>) => void` prop unchanged
   * while routing each field to the appropriate persistence path.
   */
  const handleUpdateJob = (id: string, updates: Partial<Job>) => {
    if ('messages' in updates) {
      const newMsgs = updates.messages!.filter(m => !persistedMsgIds.current.has(m.id))
      newMsgs.forEach(msg => {
        persistedMsgIds.current.add(msg.id)
        appendMessage(id, msg)
      })
      setJobs(prev =>
        prev.map(j => j.id === id ? { ...j, messages: updates.messages! } : j),
      )
    }

    // pipelineSteps, pdfDiscrepancies, isPlanningOperations, planningText are ephemeral (in-memory only)
    const ephemeralKeys = ['pipelineSteps', 'pdfDiscrepancies', 'isPlanningOperations', 'planningText'] as const
    const hasEphemeral = ephemeralKeys.some(k => k in updates)
    if (hasEphemeral) {
      setJobs(prev =>
        prev.map(j => {
          if (j.id !== id) return j
          const patch: Partial<Job> = {}
          for (const k of ephemeralKeys) {
            if (k in updates) (patch as Record<string, unknown>)[k] = (updates as Record<string, unknown>)[k]
          }
          return { ...j, ...patch }
        }),
      )
    }

    const persistedKeys = ['material', 'units', 'stockSize', 'tools', 'notes', 'operations', 'activeOperationId', 'agentSessionId', 'memory', 'extractedGeometry', 'extractedSummary'] as const
    type PersistedKey = typeof persistedKeys[number]
    const patch = Object.fromEntries(
      persistedKeys.filter(k => k in updates).map(k => [k, updates[k as PersistedKey]]),
    ) as Partial<Pick<Job, PersistedKey>>

    if (Object.keys(patch).length > 0) {
      updateJobFields(id, patch)
    }
  }

  return (
    <div className="min-h-screen bg-[#030712] text-slate-200">
      <AuthenticatedLayout isAdmin={isAdmin} onSignOut={signOut}>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/onboarding" element={<OnboardingFlow onComplete={handleOnboardingComplete} />} />
        <Route path="/report-bug" element={<BugReportPage />} />
        <Route
          path="/dashboard/*"
          element={
            <Dashboard
              onReopenJob={handleReopenJob}
              onDeleteJob={deleteJob}
              profiles={profiles}
              onAddProfile={addProfile}
              onUpdateProfile={updateProfile}
              onRemoveProfile={removeProfile}
              tools={inventoryTools}
              onChangeTools={setInventoryTools}
            />
          }
        />
        <Route
          path="/new-job"
          element={
            <MissionControl
              onAddJob={handleAddJob}
              profiles={profiles}
              onManageProfiles={handleManageProfiles}
              inventoryTools={inventoryTools}
            />
          }
        />
        <Route path="/workspace" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/workspace/:jobId"
          element={
            <WorkspaceLoader
              jobs={jobs}
              reopenJob={reopenJob}
              setActiveJobId={setActiveJobId}
              renderWorkspace={(job) => (
                <Workspace
                  job={job}
                  updateJob={updates => handleUpdateJob(job.id, updates)}
                  updateStreamingGCode={(opId, gcode) => updateStreamingOperationGCode(job.id, opId, gcode)}
                  updateOperationGCode={(opId, gcode) => updateOperationGCode(job.id, opId, gcode)}
                  addOperation={(op) => addOperation(job.id, op)}
                  removeOperation={(opId) => removeOperation(job.id, opId)}
                  profiles={profiles}
                />
              )}
            />
          }
        />
        <Route path="/admin/*" element={(isAdmin || isAdminDomain) ? <AdminDashboard /> : <Navigate to="/dashboard" replace />} />
      </Routes>
      </AuthenticatedLayout>
      <MachineProfilesModal
        open={showProfilesModal}
        profiles={profiles}
        onAdd={addProfile}
        onUpdate={updateProfile}
        onRemove={removeProfile}
        onClose={() => setShowProfilesModal(false)}
      />
    </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/share/:token" element={<ShareView />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}
